import React, { useRef, useState } from "react";
import api from "../lib/api";
import { Upload, X, Loader2, ImagePlus, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function absoluteImageUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  return `${BACKEND_URL}${u}`;
}

/**
 * ImageUploader
 *  - Big dropzone / "click to upload" → REPLACES the entire images array.
 *  - Inline "+" tile inside the preview grid → APPENDS to the existing images.
 *  - Both modes stream through the shared _upload() pipeline and call onChange()
 *    exactly once so admin form saves capture the freshest list.
 */
export default function ImageUploader({ value = [], onChange, max = 8 }) {
  const replaceInputRef = useRef(null);
  const appendInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Core upload pipeline — returns array of new URLs (or empty on failure)
  const _upload = async (files) => {
    if (!files || files.length === 0) return [];
    const uploaded = [];
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} is not an image`);
          continue;
        }
        if (file.size > 6 * 1024 * 1024) {
          toast.error(`${file.name} exceeds 6 MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        try {
          const { data } = await api.post("/admin/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploaded.push(data.url);
        } catch (err) {
          toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
        }
      }
    } finally {
      setUploading(false);
      if (replaceInputRef.current) replaceInputRef.current.value = "";
      if (appendInputRef.current) appendInputRef.current.value = "";
    }
    return uploaded;
  };

  // REPLACE — swap the whole images array with the new upload
  const handleReplace = async (files) => {
    if (!files || files.length === 0) return;
    if (files.length > max) {
      toast.error(`You can upload up to ${max} images per product`);
      return;
    }
    const uploaded = await _upload(files);
    if (uploaded.length > 0) {
      onChange(uploaded);
      toast.success(`Replaced with ${uploaded.length} new image${uploaded.length > 1 ? "s" : ""}`);
    }
  };

  // APPEND — add to the end of existing images
  const handleAppend = async (files) => {
    if (!files || files.length === 0) return;
    if (value.length + files.length > max) {
      toast.error(`You can upload up to ${max} images per product`);
      return;
    }
    const uploaded = await _upload(files);
    if (uploaded.length > 0) {
      onChange([...value, ...uploaded]);
    }
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));
  const removeAll = () => {
    if (value.length === 0) return;
    onChange([]);
    toast.success("All images removed");
  };
  const moveTo = (from, to) => {
    if (to < 0 || to >= value.length) return;
    const next = [...value];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };

  const hasImages = value.length > 0;

  return (
    <div className="space-y-3" data-testid="image-uploader">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleReplace(Array.from(e.dataTransfer.files || []));
        }}
        onClick={() => replaceInputRef.current?.click()}
        data-testid="image-uploader-dropzone"
        className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
          dragOver ? "border-welljio-accent bg-welljio-highlight" : "border-slate-200 hover:border-welljio-accent hover:bg-welljio-tint"
        }`}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-welljio-muted text-sm">
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Uploading...
          </div>
        ) : (
          <div className="text-welljio-muted text-sm">
            {hasImages ? (
              <RefreshCw className="w-5 h-5 mx-auto mb-2 text-welljio-accent" strokeWidth={1.5} />
            ) : (
              <Upload className="w-5 h-5 mx-auto mb-2 text-welljio-accent" strokeWidth={1.5} />
            )}
            <p>
              <span className="text-welljio-ink font-medium">
                {hasImages ? "Click to replace all images" : "Click to upload"}
              </span> or drag & drop
            </p>
            <p className="text-xs text-welljio-subtle mt-1">
              PNG, JPEG, WebP · up to 6 MB · max {max} images · the first image is the cover
              {hasImages && <> · <span className="text-welljio-accent font-medium">Existing images will be removed.</span></>}
            </p>
          </div>
        )}
        <input
          ref={replaceInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={(e) => handleReplace(Array.from(e.target.files || []))}
          className="hidden"
          data-testid="image-uploader-input"
        />
      </div>

      {hasImages && (
        <>
          <div className="flex items-center justify-between text-xs text-welljio-muted">
            <span>{value.length} image{value.length > 1 ? "s" : ""} attached</span>
            <button
              type="button"
              data-testid="image-uploader-remove-all"
              onClick={removeAll}
              className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-700"
            >
              <X size={12} /> Remove all
            </button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="image-uploader-preview-grid">
            {value.map((url, idx) => (
              <div key={url + idx} data-testid={`uploaded-image-${idx}`} className="relative group rounded-2xl overflow-hidden border border-slate-100 aspect-square bg-welljio-tint">
                <img src={absoluteImageUrl(url)} alt="" className="w-full h-full object-cover" />
                {idx === 0 && (
                  <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest bg-welljio-ink text-white px-2 py-0.5 rounded-full">Cover</span>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex items-center gap-1.5">
                    {idx > 0 && (
                      <button type="button" data-testid={`image-move-left-${idx}`} onClick={() => moveTo(idx, idx - 1)} aria-label="Move left" title="Move left" className="w-7 h-7 rounded-full bg-white/95 hover:bg-white text-welljio-ink flex items-center justify-center">
                        <ChevronLeft className="w-4 h-4" strokeWidth={2} />
                      </button>
                    )}
                    {idx < value.length - 1 && (
                      <button type="button" data-testid={`image-move-right-${idx}`} onClick={() => moveTo(idx, idx + 1)} aria-label="Move right" title="Move right" className="w-7 h-7 rounded-full bg-white/95 hover:bg-white text-welljio-ink flex items-center justify-center">
                        <ChevronRight className="w-4 h-4" strokeWidth={2} />
                      </button>
                    )}
                    {idx > 0 && (
                      <button type="button" data-testid={`image-make-cover-${idx}`} onClick={() => moveTo(idx, 0)} className="text-xs bg-white/95 text-welljio-ink rounded-full px-3 py-1 hover:bg-white">Set cover</button>
                    )}
                    <button type="button" data-testid={`image-remove-${idx}`} onClick={() => removeAt(idx)} className="w-7 h-7 rounded-full bg-white/95 hover:bg-white text-red-600 flex items-center justify-center">
                      <X className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {value.length < max && !uploading && (
              <button
                type="button"
                onClick={() => appendInputRef.current?.click()}
                data-testid="image-uploader-add-more"
                title="Add another image"
                className="aspect-square rounded-2xl border border-dashed border-slate-200 hover:border-welljio-accent text-welljio-muted hover:text-welljio-ink flex flex-col items-center justify-center gap-1 transition-colors"
              >
                <ImagePlus className="w-5 h-5" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-widest">Add more</span>
              </button>
            )}
          </div>
          <input
            ref={appendInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={(e) => handleAppend(Array.from(e.target.files || []))}
            className="hidden"
            data-testid="image-uploader-append-input"
          />
        </>
      )}
    </div>
  );
}
